/**
 * ParsingStep - Background job progress tracking for Bank Statement parsing
 *
 * Polls job status and shows progress. User can safely navigate away.
 */

import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { Loader2, Info, FileSpreadsheet } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;
const POLL_INTERVAL = 5000; // 5 seconds

export const ParsingStep: React.FC = () => {
  const token = localStorage.getItem('auth_token');
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    parseJobId,
    parseProgress,
    parseProgressMessage,
    setParseJobStatus,
    setCurrentStep,
    setRows,
    setStats,
    setAllLedgers,
    setError,
    fileName,
  } = useBankEntryStore();

  // Poll for job status
  useEffect(() => {
    if (!parseJobId) {
      console.error('[ParsingStep] No parseJobId found');
      setCurrentStep('select');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/jobs/${parseJobId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status: ${response.status}`);
        }

        const data = await response.json();

        // Update progress
        setParseJobStatus(data.progress || 0, data.progress_message || 'Processing...');

        // Check for completion
        if (data.status === 'completed') {
          console.log('[ParsingStep] Job completed:', data.output_data);

          // Extract results from output_data
          const output = data.output_data || {};

          // Set rows from parse results
          const rows = (output.rows || []).map((r: any) => ({
            rowIndex: r.rowIndex,
            date: r.date,
            narration: r.narration,
            debit: r.debit,
            credit: r.credit,
            ledger: r.ledger,
            vchType: r.vchType,
            confidence: r.confidence,
            isEdited: false,
            patternId: r.patternId,  // For corrections (internal, never shown)
          }));

          setRows(rows);

          // Set stats
          const stats = output.stats || {};
          setStats(
            stats.matched || 0,
            stats.llm_classified || 0,
            stats.unmatched || 0,
            stats.total || rows.length
          );

          // Set ledgers if available
          if (output.ledgers) {
            setAllLedgers(output.ledgers);
          }

          setCurrentStep('preview');
          return;
        }

        if (data.status === 'failed') {
          console.error('[ParsingStep] Job failed:', data.error_message);
          setError(data.error_message || 'Parsing failed');
          setCurrentStep('select');
          return;
        }

        // Continue polling
        pollingRef.current = setTimeout(pollStatus, POLL_INTERVAL);
      } catch (error: any) {
        console.error('[ParsingStep] Polling error:', error);
        // Continue polling despite errors
        pollingRef.current = setTimeout(pollStatus, POLL_INTERVAL);
      }
    };

    // Start polling
    pollStatus();

    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [parseJobId, token, setParseJobStatus, setCurrentStep, setRows, setStats, setAllLedgers, setError]);

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Processing Bank Statement
        </CardTitle>
        <CardDescription className="flex items-center justify-center gap-2">
          <FileSpreadsheet className="w-4 h-4" />
          {fileName || 'bank_statement.xlsx'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress bar */}
        <div className="space-y-2">
          <Progress value={parseProgress} className="h-3" />
          <p className="text-sm text-center text-muted-foreground">
            {parseProgress}% complete
          </p>
        </div>

        {/* Status message */}
        <div className="text-center">
          <p className="text-lg font-medium">
            {parseProgressMessage || 'Starting...'}
          </p>
        </div>

        {/* Time estimate */}
        <div className="text-center text-sm text-muted-foreground">
          This may take 1-2 minutes depending on file size
        </div>

        {/* Safe to leave notice */}
        <div className="flex items-center justify-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            You can safely navigate away from this page. Your file will continue processing in the background. Return anytime to check progress.
          </p>
        </div>

        {/* Job ID for reference */}
        <div className="text-center text-xs text-muted-foreground">
          Job ID: {parseJobId}
        </div>
      </CardContent>
    </Card>
  );
};

export default ParsingStep;
